import { FlightsClassTypes } from 'constants/flight'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { EmptyObject } from 'lib/object/objectUtils'
import { useMemo } from 'react'
import useAllCharterFlightJourneys from './useAllCharterFlightJourneys'
import useCharterFlightJourneysForHotelDates, { extractDepartingAndReturningFaresForClass } from './useCharterFlightJourneysForHotelDates'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'

export function useFareClassPriceDifferencesPerAdult<C extends FlightsClassTypes>({
  charterBundleRoute,
  baseClass,
  comparisonClasses: _comparisonClasses,
  checkInDate,
  checkOutDate,
  occupancies,
  selectedFareClass,
}: {
  charterBundleRoute: App.CharterBundleRoute
  baseClass: FlightsClassTypes
  comparisonClasses: Array<C>
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  selectedFareClass: FlightsClassTypes
}): Partial<Record<C, number>> {
  const comparisonClasses = useStableObjectReference(_comparisonClasses)
  const hasDates = !!(checkInDate && checkOutDate)

  const numPassengers = useMemo(() => {
    const counts = countOccupantsForFlights(occupancies)
    const total = counts.adults + counts.children + counts.infants + counts.teenagers
    return total || 1
  }, [occupancies])

  const { fareFamilies } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: selectedFareClass,
    disabled: !hasDates,
  })

  const economyFlights = useAllCharterFlightJourneys({
    charterBundleRoute,
    occupancies,
    fareClass: FlightsClassTypes.ECONOMY,
    disabled: hasDates,
  })
  const premiumFlights = useAllCharterFlightJourneys({
    charterBundleRoute,
    occupancies,
    fareClass: FlightsClassTypes.PREMIUM,
    disabled: hasDates,
  })
  const luxRowFlights = useAllCharterFlightJourneys({
    charterBundleRoute,
    occupancies,
    fareClass: FlightsClassTypes.LUXROW,
    disabled: hasDates,
  })
  const businessFlights = useAllCharterFlightJourneys({
    charterBundleRoute,
    occupancies,
    fareClass: FlightsClassTypes.BUSINESS,
    disabled: hasDates,
  })

  const cheapestPricesByClass = useMemo((): Partial<Record<FlightsClassTypes, number>> => ({
    [FlightsClassTypes.ECONOMY]: economyFlights?.metadata?.cheapest?.totalPrice,
    [FlightsClassTypes.PREMIUM]: premiumFlights?.metadata?.cheapest?.totalPrice,
    [FlightsClassTypes.LUXROW]: luxRowFlights?.metadata?.cheapest?.totalPrice,
    [FlightsClassTypes.BUSINESS]: businessFlights?.metadata?.cheapest?.totalPrice,
  }), [economyFlights, premiumFlights, luxRowFlights, businessFlights])

  return useMemo(
    () => {
      // When dates are selected, use fare families for that specific date
      if (hasDates && fareFamilies) {
        const [baseDepartingFareFamily, baseReturningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, baseClass)
        const basePricePerAdult = (baseDepartingFareFamily && baseReturningFareFamily)
          ? baseDepartingFareFamily.cost.adult + baseReturningFareFamily.cost.adult
          : 0

        if (!basePricePerAdult) {
          return EmptyObject as Partial<Record<C, number>>
        }

        return comparisonClasses.reduce((differences, fareClass) => {
          const [departingFareFamily, returningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)
          const pricePerAdult = (departingFareFamily && returningFareFamily)
            ? departingFareFamily.cost.adult + returningFareFamily.cost.adult
            : 0

          if (pricePerAdult) {
            differences[fareClass] = pricePerAdult - basePricePerAdult
          }

          return differences
        }, {} as Partial<Record<C, number>>)
      }

      // When dates are not selected, use cheapest prices from metadata
      const baseCheapestTotal = cheapestPricesByClass[baseClass]
      if (!baseCheapestTotal) {
        return EmptyObject as Partial<Record<C, number>>
      }

      const basePricePerPerson = baseCheapestTotal / numPassengers

      return comparisonClasses.reduce((differences, fareClass) => {
        const cheapestTotal = cheapestPricesByClass[fareClass]
        if (cheapestTotal !== undefined) {
          const pricePerPerson = cheapestTotal / numPassengers
          differences[fareClass] = pricePerPerson - basePricePerPerson
        }
        return differences
      }, {} as Partial<Record<C, number>>)
    },
    [hasDates, fareFamilies, cheapestPricesByClass, baseClass, comparisonClasses, numPassengers],
  )
}
